import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, fontStr } from '@/config';

const HOLD_MS = 500;
const PADDING = 16;
const MAX_BREDDE = 520;
const PEKER_HOYDE = 12;
const DEPTH = 9000;

export class TooltipManager {
  private scene?: Phaser.Scene;
  private container?: Phaser.GameObjects.Container;
  private holdTimer?: Phaser.Time.TimerEvent;
  private hoverTimer?: Phaser.Time.TimerEvent;
  private aktiv = false;

  attachScene(scene: Phaser.Scene): void {
    this.detachScene();
    this.scene = scene;
  }

  detachScene(): void {
    this.hide();
    this.holdTimer?.remove(false);
    this.holdTimer = undefined;
    this.hoverTimer?.remove(false);
    this.hoverTimer = undefined;
    this.scene = undefined;
  }

  /**
   * Hekt en tooltip på et interaktivt objekt. Tooltipen vises på
   * hold-trykk (mobil) eller hover (desktop). Hold returnerer en
   * detach-funksjon som rydder opp listenerne.
   */
  attach(
    target: Phaser.GameObjects.GameObject & {
      on: (event: string, fn: (...args: unknown[]) => void) => unknown;
      off?: (event: string, fn: (...args: unknown[]) => void) => unknown;
    },
    tekstFn: string | (() => string),
    posisjonFn?: () => { x: number; y: number },
  ): () => void {
    const hentTekst = typeof tekstFn === 'function' ? tekstFn : () => tekstFn;
    const hentPos = posisjonFn ?? (() => this.fallbackPosForTarget(target));

    const onDown = (pointer: Phaser.Input.Pointer) => {
      this.holdTimer?.remove(false);
      this.holdTimer = this.scene?.time.delayedCall(HOLD_MS, () => {
        const pos = hentPos();
        this.show(pos.x, pos.y, hentTekst());
      });
      void pointer;
    };

    const onUp = () => {
      this.holdTimer?.remove(false);
      this.holdTimer = undefined;
    };

    const onOver = () => {
      if (this.erTouchEnhet()) return;
      this.hoverTimer?.remove(false);
      this.hoverTimer = this.scene?.time.delayedCall(HOLD_MS, () => {
        const pos = hentPos();
        this.show(pos.x, pos.y, hentTekst());
      });
    };

    const onOut = () => {
      this.hoverTimer?.remove(false);
      this.hoverTimer = undefined;
      this.hide();
    };

    target.on('pointerdown', onDown);
    target.on('pointerup', onUp);
    target.on('pointerupoutside', onUp);
    target.on('pointerover', onOver);
    target.on('pointerout', onOut);

    return () => {
      target.off?.('pointerdown', onDown);
      target.off?.('pointerup', onUp);
      target.off?.('pointerupoutside', onUp);
      target.off?.('pointerover', onOver);
      target.off?.('pointerout', onOut);
    };
  }

  show(x: number, y: number, tekst: string): void {
    if (!this.scene) return;
    if (!tekst || tekst.trim().length === 0) return;
    this.hide();
    this.aktiv = true;

    const container = this.scene.add.container(0, 0).setDepth(DEPTH);

    const tempTekst = this.scene.add
      .text(0, 0, tekst, {
        fontFamily: FONTS.body,
        fontSize: fontStr(18),
        color: '#ffffff',
        wordWrap: { width: MAX_BREDDE },
        lineSpacing: 4,
      })
      .setOrigin(0, 0);

    const boksW = Math.min(MAX_BREDDE + PADDING * 2, tempTekst.width + PADDING * 2);
    const boksH = tempTekst.height + PADDING * 2;
    tempTekst.destroy();

    let boksX = x - boksW / 2;
    let boksY = y - boksH - PEKER_HOYDE - 8;

    if (boksX < 16) boksX = 16;
    if (boksX + boksW > GAME_WIDTH - 16) boksX = GAME_WIDTH - boksW - 16;
    if (boksY < 16) {
      boksY = y + PEKER_HOYDE + 8;
    }
    if (boksY + boksH > GAME_HEIGHT - 16) {
      boksY = GAME_HEIGHT - boksH - 16;
    }

    const bg = this.scene.add
      .rectangle(boksX, boksY, boksW, boksH, 0x1a1a1a, 0.94)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.wine, 1);
    container.add(bg);

    const tekstObj = this.scene.add
      .text(boksX + PADDING, boksY + PADDING, tekst, {
        fontFamily: FONTS.body,
        fontSize: fontStr(18),
        color: '#ffffff',
        wordWrap: { width: MAX_BREDDE },
        lineSpacing: 4,
      })
      .setOrigin(0, 0);
    container.add(tekstObj);

    container.alpha = 0;
    this.scene.tweens.add({
      targets: container,
      alpha: 1,
      duration: 120,
      ease: 'Cubic.easeOut',
    });

    this.container = container;
  }

  hide(): void {
    if (!this.aktiv) return;
    this.aktiv = false;
    this.container?.destroy(true);
    this.container = undefined;
  }

  get erVist(): boolean {
    return this.aktiv;
  }

  private erTouchEnhet(): boolean {
    if (typeof window === 'undefined') return false;
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  private fallbackPosForTarget(target: Phaser.GameObjects.GameObject): { x: number; y: number } {
    const obj = target as unknown as {
      x?: number;
      y?: number;
      getBounds?: () => Phaser.Geom.Rectangle;
    };
    if (obj.getBounds) {
      const b = obj.getBounds();
      return { x: b.centerX, y: b.top };
    }
    return { x: obj.x ?? GAME_WIDTH / 2, y: obj.y ?? GAME_HEIGHT / 2 };
  }
}

export const tooltipManager = new TooltipManager();
